import { ALL_OTHER_CELLS_ID, DifferentialFeaturesComparison, PinnedMorphotype, ScopeMode } from 'redux/slices'
import { FEATURES } from 'utils/constants'

export interface ScopeSummaryInput {
  scopeMode?: ScopeMode
  scopeValues?: string[]
}

/**
 * @internal
 * Get the name of the comparison for one of the selections
 *
 */

export function getComparisonNameForOneSelection(
  chosenMorphotypeIds: number[],
  pinnedCells?: PinnedMorphotype[]
): string {
  const results: string[] = []

  chosenMorphotypeIds.forEach((morphotypeId) => {
    if (morphotypeId === ALL_OTHER_CELLS_ID) {
      results.push('All Other Cells')
    } else {
      const morphotype = pinnedCells?.find((item) => item.id === morphotypeId)
      if (morphotype !== undefined) {
        results.push(morphotype.name)
      }
    }
  })

  return results.join(' & ')
}
/**
 * @internal
 * Get the name of the comparison
 * @param comparison The comparison to get the name of
 * @param pinnedCells The current pinned morphotypes
 */

export function getComparisonName(
  comparison?: DifferentialFeaturesComparison,
  pinnedCells?: PinnedMorphotype[]
): string {
  if (comparison === undefined) {
    return ''
  }

  const name1 = getComparisonNameForOneSelection(comparison.morphotypeIds1, pinnedCells)
  const name2 = getComparisonNameForOneSelection(comparison.morphotypeIds2, pinnedCells)

  return `${name1} vs. ${name2}`
}

export function getFeatureName(featureId: string): string {
  return FEATURES.find((feature) => feature.field === featureId)?.label ?? featureId
}

/**
 * @internal
 * Get the scope summary text for display.
 * Accepts either a DifferentialFeaturesComparison or a plain { scopeMode, scopeValues } object.
 * @param prefix Optional prefix for the summary text (default: 'Scope')
 */
export function getScopeSummary(input?: ScopeSummaryInput, prefix = 'Scope'): string {
  if (!input) return ''

  const { scopeMode } = input
  const scopeValues = input.scopeValues || []
  
  if (!scopeMode || scopeMode === 'ALL') {
    return `${prefix}: All cells in session`
  }

  let label = ''
  if (scopeMode === 'RUN_ID') label = 'Run ID'
  else if (scopeMode === 'SAMPLE_ID') label = 'Sample ID'
  else if (scopeMode === 'SAMPLE_TYPE') label = 'Sample Type'

  if (scopeValues.length === 0) {
    return `${prefix}: ${label} (none selected)`
  }

  if (scopeValues.length <= 2) {
    return `${prefix}: ${label} (${scopeValues.join(', ')})`
  }

  // Show first 2 values + count for more than 2
  const firstTwo = scopeValues.slice(0, 2).join(', ')
  const remaining = scopeValues.length - 2
  return `${prefix}: ${label} (${firstTwo} +${remaining} more)`
}

/**
 * @internal
 * Get the scope summary text for a comparison with per-group scopes.
 * If both scopes are identical, returns a single line. Otherwise shows both scopes.
 */
export function getDualScopeSummary(comparison?: DifferentialFeaturesComparison): string {
  if (!comparison) return ''

  const { scopeMode1, scopeValues1, scopeMode2, scopeValues2 } = comparison
  const mode1 = scopeMode1 || 'ALL'
  const mode2 = scopeMode2 || 'ALL'
  const vals1 = scopeValues1 || []
  const vals2 = scopeValues2 || []

  const scopesAreIdentical =
    mode1 === mode2 &&
    vals1.length === vals2.length &&
    vals1.every((v, i) => v === vals2[i])

  if (scopesAreIdentical) {
    return getScopeSummary({ scopeMode: mode1, scopeValues: vals1 })
  }

  const part1 = getScopeSummary({ scopeMode: mode1, scopeValues: vals1 }, 'Compare')
  const part2 = getScopeSummary({ scopeMode: mode2, scopeValues: vals2 }, 'With')
  return `${part1} · ${part2}`
}
